import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ImageIcon, Pencil, VideoIcon } from 'lucide-react';
import { useId, useState } from 'react';
import { useDecisionActionsDisabled } from '../composer-gate-context';
import { useAdCardEditChat, useAdCardEditTarget } from './ad-card-edit-context';
import { ClipPickerDialog } from './clip-picker-dialog';
import { ImagePickerDialog } from './image-picker-dialog';

/** Every pencil sends an owner-authored request through the normal Claire turn. */
export function AdCardEditButton({
  adName,
  field,
  onSendMessage,
}: {
  adName: string;
  field: string;
  onSendMessage?: (text: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  const inputId = useId();
  const [selectedImageIds, setSelectedImageIds] = useState<string[]>([]);
  const [selectedVideoIds, setSelectedVideoIds] = useState<string[]>([]);
  const [picker, setPicker] = useState<'images' | 'videos'>();
  const [error, setError] = useState<string>();
  const isCreative = field === 'creative';
  const actionsDisabled = useDecisionActionsDisabled();
  const chat = useAdCardEditChat();
  const adId = useAdCardEditTarget();
  const [sending, setSending] = useState(false);
  const disabled = actionsDisabled || chat?.disabled || sending;
  if (!onSendMessage) return null;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!sending) setOpen(next);
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={`Edit ${field}: ${adName}`}
          disabled={disabled}
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        >
          <Pencil className="size-3.5" aria-hidden="true" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-md">
        <form
          onSubmit={async (event) => {
            event.preventDefault();
            const text = instruction.trim();
            if (!text || disabled) return;
            const message = `For the ad ${JSON.stringify(adName)} shown in this card, change the ${field}: ${text}. Read the current saved ad and campaign first. Change only what I requested and keep all other copy, media, promotion, form and campaign settings unchanged.`;
            setSending(true);
            try {
              let sent = true;
              if (chat) {
                const ids = [
                  ...new Set([...selectedImageIds, ...selectedVideoIds]),
                ];
                sent = await (ids.length
                  ? chat.send(message, adId, ids)
                  : chat.send(message, adId));
              } else onSendMessage(message);
              if (sent) {
                setOpen(false);
                setInstruction('');
                setSelectedImageIds([]);
                setSelectedVideoIds([]);
                setError(undefined);
              }
            } catch (error) {
              setError(
                error instanceof Error
                  ? error.message
                  : 'Could not send this change. Try again.'
              );
            } finally {
              setSending(false);
            }
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>Edit {field}</DialogTitle>
            <DialogDescription>
              Tell Claire what to change on {adName}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor={inputId}>What would you like to change?</Label>
            <Textarea
              id={inputId}
              value={instruction}
              onChange={(event) => setInstruction(event.target.value)}
              rows={4}
              autoFocus
              disabled={sending}
            />
          </div>
          {isCreative && chat && (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={disabled}
                onClick={() => setPicker('images')}
              >
                <ImageIcon className="size-4" />
                Select images
                {selectedImageIds.length ? ` (${selectedImageIds.length})` : ''}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={disabled}
                onClick={() => setPicker('videos')}
              >
                <VideoIcon className="size-4" />
                Select videos
                {selectedVideoIds.length ? ` (${selectedVideoIds.length})` : ''}
              </Button>
            </div>
          )}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={sending}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={disabled || !instruction.trim()}>
              {sending ? 'Sending…' : 'Ask Claire'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      {picker === 'images' && (
        <ImagePickerDialog
          open
          onOpenChange={(next) => {
            if (!next) setPicker(undefined);
          }}
          selectedIds={selectedImageIds}
          minCount={0}
          maxCount={10 - selectedVideoIds.length}
          onConfirm={(ids) => {
            setSelectedImageIds(ids);
            setError(undefined);
          }}
        />
      )}
      {picker === 'videos' && (
        <ClipPickerDialog
          open
          onOpenChange={(next) => {
            if (!next) setPicker(undefined);
          }}
          selectedIds={selectedVideoIds}
          minCount={0}
          maxCount={10 - selectedImageIds.length}
          onConfirm={(ids) => {
            setSelectedVideoIds(ids);
            setError(undefined);
          }}
        />
      )}
    </Dialog>
  );
}
